/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
  withComputed,
  withHooks,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { computed, inject, signal } from '@angular/core';
import { exhaustMap, pipe, tap, combineLatest, take, switchMap } from 'rxjs';
import {
  CustomTemplateForm,
  FieldForm,
  FormElement,
  IFormField,
  IFormTemplate,
} from '../interfaces';
import { FormBuilderService } from '../services';
import {
  withRequestStatus,
  setReadStatus,
  setCreateStatus,
  setUpdateStatus,
} from '../../../../shared/store-utils/request-status.feature';
import { withErrorHandler } from '../../../../shared/store-utils/error-handler.feature';
import { tapResponse } from '@ngrx/operators';
import { NonNullableFormBuilder } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  createNewTemplateForm,
  createFieldFormFromData,
  createNewFieldForm,
  createPublishTemplate,
  createTemplateFormFromExisting,
  filterFormTemplate,
  filterFormTemplates,
  findFieldIndexInSection,
  normalizeFieldOrders,
} from '../form-builder.helpers';
import { TranslationService } from '../../../shared/services/translation.service';
import { SharedUIService } from '../../../../shared/services';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  withEntities,
  setEntity,
  setAllEntities,
  addEntity,
  removeEntity,
  setEntities,
} from '@ngrx/signals/entities';
import { withDataSync, withRouter } from '../../../../shared/store-utils';
import { withCRUD } from '../../../../shared/store-utils/crud.feature';
import { withUiStates } from '../../../../shared/store-utils/ui-state.feature';
import { deleteFormDialogData, SNACKBAR_DELETE_MESSAGE } from '../constants';
import {
  SNACKBAR_UPDATE_MESSAGE,
  UNDO_CLICKED,
} from '../../../../shared/constants';
import { TRANSLATION_KEYS } from '../constants';
import { FieldType } from '../enums';
import { PATH, StateStatus } from '../../../core/enums';
import {
  DELETE_DATA_TYPE,
  NEW_DATA_TYPE,
  UPDATE_DATA_TYPE,
} from '../../../../shared/services/data-sync/enums';

interface FormBuilderState {
  templateForm: CustomTemplateForm;
  selectedTemplateFormId: string | null;
  selectedTemplateForm: IFormTemplate | null;
  deletedField: {
    field: IFormField;
    index: number;
  } | null;
  assignedRoles: string[];
}

const initialState: FormBuilderState = {
  templateForm: createNewTemplateForm(),
  selectedTemplateFormId: null,
  selectedTemplateForm: null,
  deletedField: null,
  assignedRoles: [],
};

export const FormBuilderStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('FormBuilderStore'),
  withState(initialState),
  withEntities<IFormTemplate>(),
  withUiStates(),
  withCRUD<IFormTemplate>(),
  withRouter(),
  withRequestStatus(),
  withErrorHandler(),
  withProps(() => {
    const shouldScrollToBottom = signal(false);

    return {
      _formBuilderService: inject(FormBuilderService),
      _fb: inject(NonNullableFormBuilder),
      _translationService: inject(TranslationService),
      _sharedUIService: inject(SharedUIService),
      shouldScrollToBottom,
    };
  }),

  withComputed(({ entities, searchQuery, templateForm }) => {
    const filteredTemplates = computed(() => {
      return filterFormTemplates(entities(), searchQuery());
    });

    const hasLocationField = computed(() => {
      const form = templateForm();
      const fields =
        form.controls.sections.controls[0].controls.fields.controls;
      const types = fields.map((f) => f.controls.type.value);
      return types.includes(FieldType.LOCATION);
    });

    const hasFileField = computed(() => {
      const form = templateForm();
      const fields =
        form.controls.sections.controls[0].controls.fields.controls;
      const types = fields.map((f) => f.controls.type.value);
      return types.includes(FieldType.FILE);
    });

    return {
      filteredTemplates,
      hasLocationField,
      hasFileField,
    };
  }),
  withMethods((store) => {
    function hasChanges(): boolean {
      return store.templateForm().dirty;
    }
    function setSelectedTemplateId(id: string): void {
      const selectedTemplate = filterFormTemplate(store.entities(), id);
      const templateForm = createTemplateFormFromExisting(
        selectedTemplate,
        store._fb,
      );
      patchState(store, setEntity(selectedTemplate), {
        selectedTemplateFormId: id,
        templateForm,
      });
    }

    function resetForm(): void {
      const newTemplateForm = createNewTemplateForm();
      patchState(store, {
        templateForm: newTemplateForm,
        selectedTemplateFormId: undefined,
      });
    }

    function addField(element: FormElement, sectionIndex = 0): void {
      const section =
        store.templateForm().controls.sections.controls[sectionIndex];
      const order = section.controls.fields.controls.length + 1;
      const isLocation = element.type === FieldType.LOCATION;

      store._translationService
        .getTranslation(isLocation ? 'untitledLocation' : 'untitledQuestion')
        .pipe(take(1))
        .subscribe((translatedQuestion) => {
          const field = createNewFieldForm(
            element.type,
            order,
            translatedQuestion,
          );
          const fieldForm = createFieldFormFromData(store._fb, field);

          section.controls.fields.push(fieldForm);
          store.shouldScrollToBottom.set(true);
          normalizeFieldOrders(section);

          patchState(store, {
            templateForm: createTemplateFormFromExisting(
              store.templateForm().getRawValue(),
              store._fb,
            ),
          });

          store.templateForm().markAsDirty();
        });
    }

    function deleteField(fieldToDelete: IFormField, sectionIndex = 0): void {
      const section =
        store.templateForm().controls.sections.controls[sectionIndex];

      const fieldIndex = findFieldIndexInSection(
        store.templateForm(),
        fieldToDelete._id!,
        sectionIndex,
      );

      patchState(store, {
        deletedField: {
          field: fieldToDelete,
          index: fieldIndex,
        },
      });

      section.controls.fields.removeAt(fieldIndex);
      normalizeFieldOrders(section);

      patchState(store, {
        templateForm: createTemplateFormFromExisting(
          store.templateForm().getRawValue(),
          store._fb,
        ),
      });

      store.templateForm().markAsDirty();

      combineLatest([
        store._translationService
          .getTranslation('formFieldDeleted')
          .pipe(take(1)),
        store._translationService.getTranslation('undo').pipe(take(1)),
      ])
        .pipe(
          switchMap(([label, undoLabel]) =>
            store._sharedUIService
              .withUndoAction(label, undoLabel)
              .pipe(take(1)),
          ),
        )
        .subscribe(() => {
          const deleted = store.deletedField();
          if (deleted) {
            const { field, index } = deleted;
            const restoredFormGroup = createFieldFormFromData(store._fb, field);
            section.controls.fields.insert(index, restoredFormGroup);
            normalizeFieldOrders(section);

            patchState(store, {
              deletedField: null,
              templateForm: createTemplateFormFromExisting(
                store.templateForm().getRawValue(),
                store._fb,
              ),
            });
          }
        });
    }

    function copyField(fieldToCopy: IFormField, index: number): void {
      const copiedFormGroup = createFieldFormFromData(store._fb, fieldToCopy);
      const section = store.templateForm().controls.sections.controls[0];
      section.controls.fields.insert(index + 1, copiedFormGroup);
      normalizeFieldOrders(section);
    }

    function onFieldDrop(event: CdkDragDrop<FieldForm[]>): void {
      const controls =
        store.templateForm().controls.sections.controls[0].controls.fields
          .controls;
      moveItemInArray(controls, event.previousIndex, event.currentIndex);
      normalizeFieldOrders(store.templateForm().controls.sections.controls[0]);
    }

    function setAssignedRoles(templateId: string, roleIds: string[]) {
      patchState(store, {
        selectedTemplateFormId: templateId,
        assignedRoles: [...roleIds],
      });
    }

    function assignRole(roleId: string) {
      const currentRoles = store.assignedRoles();
      const newRoles = currentRoles.includes(roleId)
        ? currentRoles.filter((id) => id !== roleId)
        : [...currentRoles, roleId];

      patchState(store, {
        assignedRoles: [...newRoles],
      });
      store.templateForm().markAsPristine();
    }

    function getRequiredTranslations() {
      return combineLatest({
        templateTitle: store._translationService.getTranslation(
          TRANSLATION_KEYS.TEMPLATE_TITLE,
        ),
        templateDescription: store._translationService.getTranslation(
          TRANSLATION_KEYS.TEMPLATE_DESCRIPTION,
        ),
        questionTitle: store._translationService.getTranslation(
          TRANSLATION_KEYS.QUESTION_TITLE,
        ),
        fileFieldHelpText: store._translationService.getTranslation(
          TRANSLATION_KEYS.FILE_FIELD_HELP_TEXT,
        ),
        answerText: store._translationService.getTranslation(
          TRANSLATION_KEYS.ANSWER_TEXT,
        ),
        locationTitle: store._translationService.getTranslation(
          TRANSLATION_KEYS.LOCATION_TITLE,
        ),
        templateCreated: store._translationService.getTranslation(
          TRANSLATION_KEYS.TEMPLATE_CREATED,
        ),
        templateUpdated: store._translationService.getTranslation(
          TRANSLATION_KEYS.TEMPLATE_UPDATED,
        ),
      }).pipe(take(1));
    }

    const loadFormTemplates = store.loadRxMethod()(
      () => store._formBuilderService.getFormTemplates(),
      { beforeLoad: () => patchState(store, setReadStatus(StateStatus.PENDING)) },
    );

    const publishTemplate = rxMethod<CustomTemplateForm>(
      pipe(
        tap(() => patchState(store, setCreateStatus(StateStatus.PENDING))),
        exhaustMap((template) =>
          getRequiredTranslations().pipe(
            switchMap((translations) =>
              store._formBuilderService
                .createFormTemplate(
                  createPublishTemplate(template, translations),
                )
                .pipe(
                  tapResponse({
                    next: (template) => {
                      patchState(store, setEntity(template), setCreateStatus(StateStatus.FULFILLED));
                      store.navigate(PATH.FORM_BUILDER);
                      store._sharedUIService.withCloseAction(
                        translations.templateCreated,
                      );
                    },
                    error: (error: { message: string }) =>
                      patchState(store, setCreateStatus({ error: error.message })),
                  }),
                ),
            ),
          ),
        ),
      ),
    );

    const updateTemplate = rxMethod<CustomTemplateForm>(
      pipe(
        tap(() => patchState(store, setUpdateStatus(StateStatus.PENDING))),
        exhaustMap((template) =>
          getRequiredTranslations().pipe(
            switchMap((translations) =>
              store._formBuilderService
                .updateFormTemplate(
                  {
                    ...createPublishTemplate(template, translations),
                    updatedAt: new Date(),
                  },
                  store.selectedTemplateFormId()!,
                )
                .pipe(
                  tapResponse({
                    next: (template) => {
                      patchState(store, setEntity(template), setUpdateStatus(StateStatus.FULFILLED));
                      store.navigate(PATH.FORM_BUILDER);
                      store._sharedUIService.withCloseAction(
                        translations.templateUpdated,
                      );
                    },
                    error: (error: { message: string }) =>
                      patchState(store, setUpdateStatus({ error: error.message })),
                  }),
                ),
            ),
          ),
        ),
      ),
    );

    const updateAssignedRoles = rxMethod<IFormTemplate>(
      pipe(
        tap((fullTemplate) => {
          patchState(
            store,
            setEntity({ ...fullTemplate, assignedTo: store.assignedRoles() }),
            setUpdateStatus(StateStatus.PENDING),
          );
        }),
        exhaustMap((fullTemplate) => {
          const { id, sections, createdBy, createdAt, ...templateToUpdate } =
            fullTemplate;

          return store._sharedUIService
            .withUndoRequestAction(
              store._formBuilderService.updateFormTemplate(
                {
                  ...templateToUpdate,
                  assignedTo: store.assignedRoles(),
                },
                store.selectedTemplateFormId()!,
              ),
              SNACKBAR_UPDATE_MESSAGE,
            )
            .pipe(
              tapResponse({
                next: (result: IFormTemplate | typeof UNDO_CLICKED) => {
                  if (result === UNDO_CLICKED) {
                    patchState(store, setEntity(fullTemplate), setUpdateStatus(StateStatus.FULFILLED));
                    store.templateForm().markAsPristine();
                  } else {
                    patchState(store, setEntity(result), setUpdateStatus(StateStatus.FULFILLED));
                    store.templateForm().markAsPristine();
                  }
                },
                error: (error: { message: string }) =>
                  patchState(store, setUpdateStatus({ error: error.message })),
              }),
            );
        }),
      ),
    );

    const deleteTemplate = store.deleteRxMethod<IFormTemplate>()(
      (id) => store._formBuilderService.deleteFormTemplate(id),
      SNACKBAR_DELETE_MESSAGE,
      deleteFormDialogData,
      undefined,
      (template) => {
        const currentEntities = store.entities();
        patchState(
          store,
          setAllEntities([...currentEntities, template]),
        );
      },
    );

    const getFormTemplateById = rxMethod<string>(
      pipe(
        tap(() => patchState(store, setReadStatus(StateStatus.PENDING))),
        exhaustMap((id) =>
          store._formBuilderService.getFormTemplateById(id).pipe(
            tapResponse({
              next: (template) =>
                patchState(
                  store,
                  setEntity(template),
                  {
                    selectedTemplateFormId: id,
                    selectedTemplateForm: template,
                    templateForm: createTemplateFormFromExisting(
                      template,
                      store._fb,
                    ),
                  },
                  setReadStatus(StateStatus.FULFILLED),
                ),
              error: (error: { message: string }) =>
                patchState(store, setReadStatus({ error: error.message })),
            }),
          ),
        ),
      ),
    );

    return {
      hasChanges,
      setSelectedTemplateId,
      addField,
      deleteField,
      copyField,
      onFieldDrop,
      setAssignedRoles,
      assignRole,
      loadFormTemplates,
      publishTemplate,
      updateTemplate,
      deleteTemplate,
      getFormTemplateById,
      updateAssignedRoles,
      getRequiredTranslations,
      resetForm,
    };
  }),
  withDataSync({
    newDataTypes: {
      [NEW_DATA_TYPE.TEMPLATE]: (data, store) => {
        const idx = store
          .entities()
          .findIndex((e: IFormTemplate) => e.id === data.data.id);
        if (idx === -1) patchState(store, addEntity(data.data));
      },
    },
    updateDataTypes: {
      [UPDATE_DATA_TYPE.TEMPLATE]: (data, store) => {
        const idx = store
          .entities()
          .findIndex((e: IFormTemplate) => e.id === data.templateId);
        if (idx === -1) return;
        const entitiesRpl = store.entities();
        entitiesRpl[idx] = data.data;
        patchState(store, setEntities(entitiesRpl));
      },
    },
    deleteDataTypes: {
      [DELETE_DATA_TYPE.TEMPLATE]: (data, store) => {
        const idx = store
          .entities()
          .findIndex((e: IFormTemplate) => e.id === data.templateId);
        if (idx !== -1) patchState(store, removeEntity(data.templateId));
      },
    },
  }),
  withHooks({
    onInit({ initializeDataSync }) {
      initializeDataSync();
    },
  }),
);
